import { useSelector } from "react-redux";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import useDynamicSnackbar from './useDynamicSnackbar.ts';
import { SnackbarTypeEnum } from './SnackbarTypeEnum.ts';
import { FC, useEffect, useState } from 'react';
import Grow, { GrowProps } from '@mui/material/Grow';
import { SnackbarCloseReason } from '@mui/material/Snackbar/Snackbar';

function GrowTransition(props: GrowProps) {
  return <Grow {...props} />;
}

type DynamicSnackbarInputProps = {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
  defaultAutoHideDuration: number;
};

const DynamicSnackbar: FC<DynamicSnackbarInputProps> = ({vertical = 'top', horizontal = 'center', defaultAutoHideDuration = 6000 }) => {
  const { type, body, autoHideDuration } = useSelector((state: {'snackbar': any}) => state.snackbar);
  const actualAutoHideDuration = autoHideDuration ?? defaultAutoHideDuration;
  const { closeSnackbar } = useDynamicSnackbar();
  const [progress, setProgress] = useState(0);
  const [currentType, setCurrentType] = useState(SnackbarTypeEnum.NONE);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  const handleClick = () => {
    closeSnackbar()
  }

  const handleClose = (_?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') {
      return
    }
    closeSnackbar()
  }

  useEffect(() => {
    if (type === SnackbarTypeEnum.NONE) {
      if (timer) {
        clearInterval(timer)
        setTimer(null)
      }
      setProgress(0)
      setCurrentType(type)
    } else {
      if (currentType !== type) {
        if (timer) {
          clearInterval(timer)
          setTimer(null)
        }
        setProgress(0)
        setCurrentType(type)
      }

      const steps = actualAutoHideDuration / 50;

      const timerInterval = setInterval(() => {
        setProgress((prevProgress) => (prevProgress >= 100 ? 100 : prevProgress + 2));
      }, steps);
      setTimer(timerInterval);

      return () => {
        if (timer) {
          clearInterval(timer)
          setTimer(null)
        }
      };
    }
  }, [actualAutoHideDuration, type]);

  return (
    <Snackbar
      key={body}
      anchorOrigin={{ vertical, horizontal }}
      open={currentType !== SnackbarTypeEnum.NONE}
      TransitionComponent={GrowTransition}
      onClose={handleClose}
      autoHideDuration={actualAutoHideDuration}
    >
      <Alert
        onClose={handleClick}
        severity={type}
        variant="filled"
        sx={{ width: '100%' }}
        aria-label="snackbar alert"
      >
        { body }
        <LinearProgress variant="determinate" value={progress} />
      </Alert>
    </Snackbar>
  );
}

export default DynamicSnackbar;
